import { useEffect, useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import useReportingPeriod from './useReportingPeriod'

/**
 * Hook that enforces report month restrictions by redirecting users away from
 * past or future months to the current valid reporting month.
 *
 * @returns {boolean} isValidated - Whether the month has been validated (true = safe to render, false = still checking/redirecting)
 */
const useReportMonthGuard = (): boolean => {
  const [isValidated, setIsValidated] = useState(false)
  const { month } = useParams<{ month: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const reportingPeriod = useReportingPeriod()

  // Extract step from pathname since routes use hardcoded steps
  const step = location.pathname.split('/')[3]

  useEffect(() => {
    // Only proceed when reporting period data is ready and we have both month and step params
    if (!reportingPeriod.isReady || !month || !step) {
      return
    }

    // Validate month format (YYYY-MM)
    if (!month.match(/^\d{4}-\d{2}$/)) {
      // Invalid format - navigate to 404
      navigate('/notfound', { replace: true })
      return
    }

    const currentValidMonth = reportingPeriod.month

    // If user is trying to access a different month than the current valid one,
    // redirect them to the current month while preserving the step
    if (month !== currentValidMonth) {
      navigate(`/report/${currentValidMonth}/${step}`, { replace: true })
      return
    }

    // Month is valid, allow rendering
    setIsValidated(true)
  }, [
    month,
    step,
    navigate,
    location.pathname,
    reportingPeriod.isReady,
    reportingPeriod.month,
  ])

  return isValidated
}

export default useReportMonthGuard
